// ---------------------------------------------------------------------------
// EvalModeCard — dropdown selector for the template's evaluation mode:
// single-run, compare-agents, or compare-tasks. Locked in edit mode
// because changing the mode reshapes the tasks list destructively.
// ---------------------------------------------------------------------------

import { Dropdown, Option } from '@fluentui/react-components';

import { useStyles } from '../../EvaluationTemplateForm.style.js';
import {
  EVALUATION_MODE_OPTIONS,
  type EvaluationMode,
} from '../../EvaluationTemplateForm.types.js';

export interface EvalModeCardProps {
  evalMode: EvaluationMode;
  /** True in edit mode — locks the dropdown. */
  locked: boolean;
  onChange: (next: EvaluationMode) => void;
}

export function EvalModeCard({
  evalMode,
  locked,
  onChange,
}: EvalModeCardProps) {
  const styles = useStyles();
  const selected = EVALUATION_MODE_OPTIONS.find((o) => o.value === evalMode);
  return (
    <section className={styles.card} aria-labelledby="card-mode">
      <div id="card-mode" className={styles.cardLabel}>
        Evaluation mode
      </div>
      <div className={styles.cardBody}>
        <Dropdown
          className={styles.compactSelect}
          aria-label="Evaluation mode"
          disabled={locked}
          value={selected?.title ?? ''}
          selectedOptions={[evalMode]}
          onOptionSelect={(_e, data) => {
            if (data.optionValue) {
              onChange(data.optionValue as EvaluationMode);
            }
          }}
        >
          {EVALUATION_MODE_OPTIONS.map((opt) => {
            // Only single-run is supported for now; the comparison modes
            // are surfaced but disabled.
            const optionDisabled = opt.value !== 'single-run';
            return (
              <Option
                key={opt.value}
                value={opt.value}
                text={opt.title}
                disabled={optionDisabled}
              >
                <div className={styles.optionStack}>
                  <span className={styles.segmentTitle}>{opt.title}</span>
                  <span className={styles.segmentDescription}>
                    {opt.description}
                  </span>
                  {optionDisabled && (
                    <span className={styles.segmentNote}>
                      Not available yet
                    </span>
                  )}
                </div>
              </Option>
            );
          })}
        </Dropdown>
      </div>
    </section>
  );
}
